import { useMemo } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoStoriesIcon from '@mui/icons-material/AutoStoriesOutlined';
import BugReportIcon from '@mui/icons-material/BugReportOutlined';
import GroupsIcon from '@mui/icons-material/GroupsOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAltOutlined';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import { useTheme } from '@mui/material/styles';
import {
  BarChart, LineChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, Legend, ResponsiveContainer, ReferenceLine, LabelList,
} from 'recharts';
import { useTrend } from '../hooks/useTrend';
import { useFilters } from '../store/filters';
import { buildMonthKeys, monthLabel, currentMonthKey } from '../lib/months';
import { fmtInt } from '../lib/format';
import { emptyMonth } from '../lib/trend';
import { useT, format } from '../i18n';
import { renderInline } from '../lib/inline-md';
import type { ReactNode } from 'react';

// Farbpaletten pro Typ. Bei Bugs: klare Hue-Trennung zwischen Kunde (rot,
// Signalfarbe) und intern (orange, warm aber deutlich unterscheidbar).
// Created = gesättigt, Closed = pastell.
const STORY_PALETTE = { created: '#1b5e20', resolved: '#81c784', line: '#1b5e20', icon: '#2e7d32' };
const BUG_PALETTE   = {
  created:         '#c62828',  // Fallback / Total-Farbe für Δ-Linie und Chips
  createdCustomer: '#c62828',  // Kunden-Bugs Created (kräftiges Rot)
  createdInternal: '#ef6c00',  // interne Bugs Created (Orange)
  resolved:        '#ef9a9a',  // Fallback
  resolvedCustomer:'#ef9a9a',  // Kunden-Bugs Closed (hellrot/rosa)
  resolvedInternal:'#ffcc80',  // interne Bugs Closed (hellorange)
  line: '#b71c1c',
  icon: '#c62828',
};

// Identische Margin- und Y-Achsen-Breite für Trend-Linie und Bar-Chart,
// damit die Monats-Spalten beider SubCharts exakt aligned sind.
const CHART_MARGIN = { top: 16, right: 8, bottom: 0, left: -8 } as const;
const Y_WIDTH = 48;

type Kind = 'story' | 'bug';

// Δ% = (resolved − created) / max(created, resolved, 1) × 100
// Symmetrisch in [-100, +100]. Über 0 = Backlog schrumpft (gut).
// Bei created = resolved = 0 → null (Lücke in der Linie).
function delta(created: number, resolved: number): number | null {
  if (created === 0 && resolved === 0) return null;
  const denom = Math.max(created, resolved, 1);
  return ((resolved - created) / denom) * 100;
}

// Container: gemeinsamer Filter-Chip-Header + zwei Charts (Stories / Bugs).
export function TrendChart() {
  const { available } = useTrend();
  if (!available.created && !available.resolved) return null;

  return (
    <Stack spacing={2}>
      <IssueTypeChart kind="story" />
      <IssueTypeChart kind="bug" />
    </Stack>
  );
}

function IssueTypeChart({ kind }: { kind: Kind }) {
  const { index } = useTrend();
  const months = useMemo(() => buildMonthKeys(), []);
  const cur = currentMonthKey();
  const theme = useTheme();
  const t = useT();

  const team     = useFilters(s => s.team);
  const assignee = useFilters(s => s.assignee);
  const status   = useFilters(s => s.status);
  const priority = useFilters(s => s.priority);
  const search   = useFilters(s => s.search);

  const palette = kind === 'story' ? STORY_PALETTE : BUG_PALETTE;
  const title   = kind === 'story' ? 'Stories' : 'Bugs';
  const Icon    = kind === 'story' ? AutoStoriesIcon : BugReportIcon;

  const data = months.map(mk => {
    const v = index.get(mk) ?? emptyMonth();
    const c = kind === 'story' ? v.openedStories : v.openedBugs;
    const r = kind === 'story' ? v.closedStories : v.closedBugs;
    return {
      mk,
      label: monthLabel(mk),
      isCurrent: mk === cur,
      created:  c,
      resolved: r,
      // Aufsplittung nur bei Bugs; bei Stories bleiben die Werte 0.
      createdCustomer:  kind === 'bug' ? v.openedCustomerBugs : 0,
      createdInternal:  kind === 'bug' ? v.openedInternalBugs : 0,
      resolvedCustomer: kind === 'bug' ? v.closedCustomerBugs : 0,
      resolvedInternal: kind === 'bug' ? v.closedInternalBugs : 0,
      deltaPct: delta(c, r),
    };
  });

  // Totale für die Kopf-Zeile (über alle 18 Monate)
  const totalCreated  = data.reduce((s, d) => s + d.created,  0);
  const totalResolved = data.reduce((s, d) => s + d.resolved, 0);
  const overallDelta  = delta(totalCreated, totalResolved);

  const infoText = kind === 'story' ? t.trend.infoTextStories : t.trend.infoTextBugs;

  return (
    <Card>
      <CardContent>
        {/* Kopf-Zeile */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 36, height: 36, borderRadius: '50%',
              bgcolor: palette.icon, color: 'white',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="overline" color="text.secondary">Trend</Typography>
            <Typography variant="h6" sx={{ lineHeight: 1.1 }}>{title}</Typography>
          </Box>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <SummaryChip label={t.trend.sumCreated} value={fmtInt(totalCreated)}  color={palette.created} />
            <SummaryChip label={t.trend.sumClosed}  value={fmtInt(totalResolved)} color={palette.resolved} />
            <SummaryChip
              label={t.trend.deltaTotal}
              value={overallDelta == null ? '–' : `${overallDelta > 0 ? '+' : ''}${Math.round(overallDelta)}%`}
              color={overallDelta == null ? theme.palette.text.secondary : (overallDelta >= 0 ? '#2e7d32' : '#c62828')}
            />
          </Stack>
          <Tooltip arrow title={<Box sx={{ whiteSpace: 'pre-line', fontSize: 12, maxWidth: 320 }}>{infoText}</Box>}>
            <IconButton size="small"><InfoOutlinedIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>

        {/* Aktive Filter prominent */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
          {team ? (
            <Chip icon={<GroupsIcon />} label={`${t.banner.teamPrefix}: ${team}`} color="primary" size="small" sx={{ fontWeight: 700 }} />
          ) : (
            <Chip icon={<GroupsIcon />} label={t.trend.allTeamsChip} size="small" variant="outlined" />
          )}
          {status   && <Chip icon={<FilterAltIcon />} size="small" color="warning" label={`${t.banner.statusPrefix}: ${status}`} />}
          {assignee && <Chip icon={<FilterAltIcon />} size="small" color="warning" label={`${t.banner.assigneePrefix}: ${assignee}`} />}
          {priority && <Chip icon={<FilterAltIcon />} size="small" color="warning" label={`${t.banner.priorityPrefix}: ${priority}`} />}
          {search   && <Chip icon={<SearchIcon />}    size="small" color="warning" label={`${t.banner.searchPrefix}: "${search}"`} />}
        </Stack>

        {/* Definition der geschlossenen Tickets — permanent sichtbar */}
        <Box
          sx={{
            mb: 2,
            p: 1,
            borderRadius: 1.5,
            bgcolor: 'action.hover',
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {renderInline(t.trend.closedDefinition)}
          </Typography>
        </Box>

        {/* Chart-Box: oben Δ%-Linie mit Zahlen-Labels, unten Bars.
            Beide Charts teilen Margins und YAxis.width, damit die
            Monats-Spalten visuell exakt aligned bleiben. */}
        <Box sx={{ width: '100%' }}>
          {/* Δ%-Linie obendrüber */}
          <Box sx={{ width: '100%', height: 110 }}>
            <ResponsiveContainer>
              <LineChart data={data} margin={CHART_MARGIN}>
                <ReferenceLine y={0} stroke={theme.palette.divider} strokeDasharray="2 4" />
                <XAxis dataKey="label" hide />
                <YAxis
                  domain={[-100, 100]}
                  ticks={[-100, 0, 100]}
                  tickFormatter={(v: number) => `${v > 0 ? '+' : ''}${v}%`}
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  axisLine={false}
                  tickLine={false}
                  width={Y_WIDTH}
                />
                <ReTooltip content={<TypeTooltip />} />
                <Line
                  name="Δ%"
                  type="monotone"
                  dataKey="deltaPct"
                  stroke={palette.line}
                  strokeWidth={2.5}
                  dot={{ r: 4, strokeWidth: 0, fill: palette.line }}
                  connectNulls
                >
                  <LabelList
                    dataKey="deltaPct"
                    position="top"
                    offset={8}
                    formatter={(v: unknown) => {
                      if (typeof v !== 'number') return '';
                      return `${v > 0 ? '+' : ''}${Math.round(v)}%`;
                    }}
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      fill: theme.palette.text.primary,
                      paintOrder: 'stroke',
                      stroke: theme.palette.background.paper,
                      strokeWidth: 4,
                    }}
                  />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Bars darunter */}
          <Box sx={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={data} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  axisLine={{ stroke: theme.palette.divider }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                  axisLine={{ stroke: theme.palette.divider }}
                  tickLine={false}
                  width={Y_WIDTH}
                />
                <ReTooltip content={<TypeTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} iconType="square" />
                {kind === 'story' ? (
                  <>
                    <Bar name={format(t.trend.barCreated, { title })} dataKey="created"  fill={palette.created} />
                    <Bar name={format(t.trend.barClosed,  { title })} dataKey="resolved" fill={palette.resolved} />
                  </>
                ) : (
                  <>
                    <Bar
                      name="Bugs created · Kunde"
                      dataKey="createdCustomer"
                      stackId="created"
                      fill={BUG_PALETTE.createdCustomer}
                    />
                    <Bar
                      name="Bugs created · intern"
                      dataKey="createdInternal"
                      stackId="created"
                      fill={BUG_PALETTE.createdInternal}
                    />
                    <Bar
                      name="Bugs closed · Kunde"
                      dataKey="resolvedCustomer"
                      stackId="resolved"
                      fill={BUG_PALETTE.resolvedCustomer}
                    />
                    <Bar
                      name="Bugs closed · intern"
                      dataKey="resolvedInternal"
                      stackId="resolved"
                      fill={BUG_PALETTE.resolvedInternal}
                    />
                  </>
                )}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Box>

        {/* Footer-Caption */}
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          {t.trend.footerReading}
          <br />
          {t.trend.footerBars}
          {' · '}
          {team ? format(t.trend.footerTeamFiltered, { team }) : t.trend.footerAllTeams}
        </Typography>
      </CardContent>
    </Card>
  );
}

function SummaryChip({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 60 }}>
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>{label}</Typography>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Box>
  );
}

// Custom-Tooltip mit %-Formatierung für die Δ%-Linie.
type RechartsTooltipProps = {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string; dataKey: string }>;
  label?: string;
};

function TypeTooltip({ active, payload, label }: RechartsTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: 3,
        p: 1.5,
        minWidth: 180,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 0.75 }}>{label}</Typography>
      <Stack spacing={0.5}>
        {payload.map((p, i) => {
          const isPct = p.dataKey === 'deltaPct';
          const text: ReactNode = isPct
            ? (p.value == null ? '–' : `${p.value > 0 ? '+' : ''}${Math.round(p.value)}%`)
            : fmtInt(p.value);
          return (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: p.color }} />
              <Typography variant="caption" sx={{ flex: 1 }}>{p.name}</Typography>
              <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                {text}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
